import React, { useEffect, useState, useCallback } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import { Play, ExternalLink } from "lucide-react";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

const API_KEY = process.env.REACT_APP_YOUTUBE_API_KEY;
const CHANNEL_HANDLE = "fastridercycles";

const VideoSection = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchVideos = useCallback(async () => {
    try {
      setError("");

      // Get channel details
      const channelRes = await fetch(
        `https://www.googleapis.com/youtube/v3/channels?part=contentDetails&forHandle=${CHANNEL_HANDLE}&key=${API_KEY}`,
      );

      const channelData = await channelRes.json();

      if (!channelRes.ok || !channelData.items?.length) {
        throw new Error("Unable to find YouTube channel.");
      }

      const uploadsPlaylistId =
        channelData.items[0].contentDetails.relatedPlaylists.uploads;

      // Get latest uploads
      const playlistRes = await fetch(
        `https://www.googleapis.com/youtube/v3/playlistItems?part=snippet,contentDetails&playlistId=${uploadsPlaylistId}&maxResults=20&key=${API_KEY}`,
      );

      const playlistData = await playlistRes.json();

      if (!playlistRes.ok) {
        throw new Error("Unable to fetch YouTube videos.");
      }

      const videoIds = playlistData.items
        .map((item) => item.contentDetails?.videoId)
        .filter(Boolean);

      if (!videoIds.length) {
        setVideos([]);
        return;
      }

      // Get video details
      const videosRes = await fetch(
        `https://www.googleapis.com/youtube/v3/videos?part=snippet,contentDetails&id=${videoIds.join(
          ",",
        )}&key=${API_KEY}`,
      );

      const videosData = await videosRes.json();

      if (!videosRes.ok) {
        throw new Error("Unable to fetch video details.");
      }

      // Filter videos up to 3 minutes
      const shorts = videosData.items
        .filter((video) => {
          const duration = parseYouTubeDuration(video.contentDetails.duration);

          return duration <= 180;
        })
        .sort(
          (a, b) =>
            new Date(b.snippet.publishedAt) - new Date(a.snippet.publishedAt),
        )
        .slice(0, 10)
        .map((video) => ({
          id: video.id,
          title: video.snippet.title,
          thumbnail:
            video.snippet.thumbnails?.high?.url ||
            video.snippet.thumbnails?.medium?.url,
          publishedAt: video.snippet.publishedAt,
        }));

      setVideos(shorts);
    } catch (err) {
      console.error("YouTube API Error:", err);
      setError(err.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!API_KEY) {
      setError("YouTube API key is missing.");
      setLoading(false);
      return;
    }

    fetchVideos();

    // Check for new videos every 10 minutes
    const interval = setInterval(
      () => {
        fetchVideos();
      },
      10 * 60 * 1000,
    );

    return () => clearInterval(interval);
  }, [fetchVideos]);

  return (
    <section className="custom-video-section">
      <div className="container">
        {/* Heading */}
        <div className="custom-video-heading">
          <h2>Ride. Watch. Repeat.</h2>
        </div>

        {/* Loading */}
        {loading && (
          <div className="custom-video-status">
            <div className="custom-video-spinner"></div>
            <p>Loading latest videos...</p>
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="custom-video-status custom-video-error">
            <p>{error}</p>
          </div>
        )}

        {/* Videos */}
        {!loading && !error && videos.length > 0 && (
          <div className="custom-video-slider">
            <Swiper
              modules={[Navigation, Pagination, Autoplay]}
              pagination={{
                clickable: true,
              }}
              autoplay={{
                delay: 4000,
                disableOnInteraction: false,
              }}
              loop={videos.length > 4}
              spaceBetween={20}
              slidesPerView={4}
              breakpoints={{
                0: {
                  slidesPerView: 1.15,
                },
                576: {
                  slidesPerView: 2,
                },
                768: {
                  slidesPerView: 3,
                },
                1200: {
                  slidesPerView: 4,
                },
              }}
              className="custom-video-swiper"
            >
              {videos.map((video) => (
                <SwiperSlide key={video.id}>
                  <div className="custom-video-card">
                    <a
                      href={`https://www.youtube.com/shorts/${video.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="custom-video-link"
                    >
                      <div className="custom-video-image">
                        <img
                          src={video.thumbnail}
                          alt={video.title}
                          loading="lazy"
                        />

                        <div className="custom-video-overlay">
                          <div className="custom-video-play">
                            <Play size={22} fill="currentColor" />
                          </div>
                        </div>

                        <span className="custom-video-badge">SHORT</span>
                      </div>
                    </a>

                    <div className="custom-video-content">
                      <h3 title={video.title}>{video.title}</h3>

                      <div className="custom-video-bottom">
                        <span>{formatDate(video.publishedAt)}</span>

                        <a
                          href={`https://www.youtube.com/shorts/${video.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="custom-video-youtube"
                        >
                          <ExternalLink size={16} />
                        </a>
                      </div>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        )}

        {/* No videos */}
        {!loading && !error && videos.length === 0 && (
          <div className="custom-video-status">
            <p>No Shorts found.</p>
          </div>
        )}
      </div>
    </section>
  );
};

/* ============================
   YouTube Duration Parser
============================ */

const parseYouTubeDuration = (duration) => {
  const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);

  if (!match) return 0;

  const hours = Number(match[1] || 0);
  const minutes = Number(match[2] || 0);
  const seconds = Number(match[3] || 0);

  return hours * 3600 + minutes * 60 + seconds;
};

/* ============================
   Date Formatter
============================ */

const formatDate = (date) => {
  return new Date(date).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

export default VideoSection;
